/* Announcement schedules use the store's Pacific wall clock, including daylight saving time.
 * Keep source messages inert so cached HTML cannot flash an expired/future announcement.
 * Rebuild the native carousel only when eligibility changes: its private selected index
 * and autoplay state must never refer to a slide that has just expired.
 */
(() => {
  if (customElements.get('bb-announcement-schedule')) return;

  function parseDateTime(value) {
    const text = (value || '').trim();
    if (!text) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(text);
    if (!match) return false;
    const [, y, m, d, h, min, sec = '00'] = match;
    const date = new Date(`${y}-${m}-${d}T${h}:${min}:${sec}Z`);
    if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 19) !== `${y}-${m}-${d}T${h}:${min}:${sec}`) return false;
    return `${y}-${m}-${d} ${h}:${min}:${sec}`;
  }

  class AnnouncementSchedule extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.timer) return;
        this.template = this.querySelector('template');
        this.outlet = this.querySelector('[data-announcement-outlet]');
        if (!this.template || !this.outlet) return;
        this.formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: this.dataset.timeZone || 'America/Los_Angeles',
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
        });
        this.messages = [...this.template.content.querySelectorAll('[data-announcement-id]')].map(node => ({
          id: node.dataset.announcementId,
          start: parseDateTime(node.dataset.startDatetime),
          end: parseDateTime(node.dataset.expirationDatetime)
        }));
        this.signature = null;
        this.abortController = new AbortController();
        this.refresh = this.refresh.bind(this);
        document.addEventListener('visibilitychange', this.refresh, { signal: this.abortController.signal });
        window.addEventListener('pageshow', this.refresh, { signal: this.abortController.signal });
        this.refresh();
        this.timer = setInterval(this.refresh, 1000);
      });
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.timer = null;
      this.abortController?.abort();
      this.querySelector('announcement-bar')?.player?.stop();
    }

    refresh() {
      const parts = Object.fromEntries(this.formatter.formatToParts(new Date()).map(p => [p.type, p.value]));
      const now = `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
      const statuses = this.messages.map(message => {
        if (message.start === false || message.end === false || (message.start && message.end && message.end <= message.start)) return 'Invalid schedule';
        if (message.start && now < message.start) return 'Scheduled';
        if (message.end && now >= message.end) return 'Expired';
        return 'Active';
      });
      const designMode = Boolean(window.Shopify?.designMode);
      const activeIds = this.messages.filter((message, index) => designMode || statuses[index] === 'Active').map(message => message.id);
      const signature = JSON.stringify(designMode ? statuses : activeIds);
      if (this.signature === signature) return;
      this.signature = signature;

      const previous = this.outlet.querySelector('announcement-bar');
      const selectedId = previous?.querySelector('.is-selected')?.dataset.announcementId;
      previous?.player?.stop();
      const fragment = this.template.content.cloneNode(true);
      fragment.querySelectorAll('[data-announcement-id]').forEach(node => {
        if (!activeIds.includes(node.dataset.announcementId)) {
          node.remove();
          return;
        }
        node.classList.remove('is-selected');
        if (designMode) {
          const index = this.messages.findIndex(message => message.id === node.dataset.announcementId);
          const note = document.createElement('p');
          note.style.cssText = 'font-size:11px;line-height:1.4;margin:4px 0;text-transform:none;letter-spacing:normal';
          note.textContent = `${statuses[index]} · Pacific time · Start: ${node.dataset.startDatetime.trim() || 'immediately'} · Expiration: ${node.dataset.expirationDatetime.trim() || 'none'} (editor preview)`;
          node.append(note);
        }
      });
      if (activeIds.length) {
        const cells = [...fragment.querySelectorAll('[data-announcement-id]')];
        (cells.find(node => node.dataset.announcementId === selectedId) || cells[0]).classList.add('is-selected');
        if (activeIds.length === 1) {
          fragment.querySelectorAll('carousel-prev-button, carousel-next-button').forEach(node => node.remove());
          fragment.querySelector('announcement-bar').removeAttribute('autoplay');
        }
        this.outlet.replaceChildren(fragment);
      } else {
        this.outlet.replaceChildren();
      }
      const section = this.closest('.shopify-section');
      if (section) document.documentElement.style.setProperty('--announcement-bar-height', `${Math.round(section.getBoundingClientRect().height)}px`);
    }
  }

  customElements.define('bb-announcement-schedule', AnnouncementSchedule);
})();
